import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UnifiedTopBar from './UnifiedTopBar';
import { createCreateFormTemplate } from './templates';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';

export interface CreateFormTopBarConfig {
  title?: string;
  routeFlagKey: string;
  paramName?: string;
  pageContextKey?: string;
  cancelPath?: string;
  createPath?: string;
  backPath?: string;
  saveAriaLabel?: string;
  useCreateButton?: boolean;
}

interface CreateFormTopBarProps {
  config: CreateFormTopBarConfig;
  onSave: () => void;
}

const CreateFormTopBar: React.FC<CreateFormTopBarProps> = ({
  config,
  onSave,
}) => {
  const params = useParams();
  const navigate = useNavigate();

  const paramValue = config.paramName ? params[config.paramName] : undefined;

  const handleCancel = useCallback(() => {
    if (config.cancelPath && paramValue) {
      const path = config.cancelPath.replace(
        `{${config.paramName}}`,
        paramValue
      );
      navigate(path);
    } else if (config.cancelPath) {
      // Direct navigation for paths without parameters
      navigate(config.cancelPath);
    } else if (config.backPath && paramValue) {
      const path = config.backPath.replace(`{${config.paramName}}`, paramValue);
      navigate(path);
    }
  }, [config, paramValue, navigate]);

  const pageContext = useMemo(() => {
    const context: any = {
      route: config.routeFlagKey,
    };

    if (config.paramName && paramValue) {
      const contextKey = config.pageContextKey || config.paramName;
      context[contextKey] = paramValue;
    }

    return context;
  }, [
    config.routeFlagKey,
    config.paramName,
    config.pageContextKey,
    paramValue,
  ]);

  const unifiedConfig = useMemo(() => {
    const templateOptions: Parameters<typeof createCreateFormTemplate>[0] = {
      title: config.title,
      onSave,
      onCancel: handleCancel,
      saveAriaLabel: config.saveAriaLabel,
      cancelAriaLabel: 'Back',
      pageContext,
      saveElement: config.useCreateButton
        ? (
            <CreateButton
              onClick={onSave}
              text={config.saveAriaLabel || 'Create'}
              className="topBar-create-button"
            />
          )
        : undefined,
    };

    return createCreateFormTemplate(templateOptions);
  }, [
    config.title,
    config.saveAriaLabel,
    onSave,
    handleCancel,
    pageContext,
    config.useCreateButton,
  ]);

  return <UnifiedTopBar {...unifiedConfig} />;
};

export default CreateFormTopBar;
